import {
  createChatTrigger,
  deleteChatTrigger,
  listChatDiscounts,
  listChatTriggers,
  updateChatTrigger,
  type ChatDiscountOptions,
  type ChatTrigger,
  type CreateChatTriggerRequest,
  type UpdateChatTriggerRequest,
  createChatBan,
  createChatDepartment,
  createChatShortcut,
  deleteChatBan,
  deleteChatDepartment,
  deleteChatShortcut,
  getChatSettings,
  getMyChatProfile,
  listChatBans,
  listChatDepartments,
  listChatShortcuts,
  updateChatDepartment,
  updateChatSettings,
  updateChatShortcut,
  updateMyChatProfile,
  type ChatBan,
  type ChatDepartment,
  type ChatSettings,
  type ChatShortcut,
  type CreateChatBanRequest,
  type CreateChatDepartmentRequest,
  type CreateChatShortcutRequest,
  type MemberChatProfile,
  type UpdateChatDepartmentRequest,
  type UpdateChatSettingsRequest,
  type UpdateChatShortcutRequest,
  type UpdateMemberChatProfileRequest,
} from '@nucleus-commerce/api-client/admin';

export interface ChatBanPage {
  items: ChatBan[];
  nextCursor: string | null;
}

/**
 * The chat setup surface (LC0, LCW1): the store's chat settings have their own route (read with
 * `conversations.read`, patched with `conversations.manage`, merged one group deep, answering the
 * whole settings), shortcuts and departments are bounded `{ items }` lists, bans are cursor pages.
 */
export const chatSettingsApi = {
  settings: (storeId: string): Promise<ChatSettings> => getChatSettings({ storeId }),
  updateSettings: (storeId: string, patch: UpdateChatSettingsRequest): Promise<ChatSettings> =>
    updateChatSettings({ storeId }, patch),

  shortcuts: async (storeId: string): Promise<ChatShortcut[]> =>
    (await listChatShortcuts({ storeId })).items,
  createShortcut: (storeId: string, body: CreateChatShortcutRequest): Promise<ChatShortcut> =>
    createChatShortcut({ storeId }, body),
  updateShortcut: (
    storeId: string,
    id: string,
    body: UpdateChatShortcutRequest,
  ): Promise<ChatShortcut> => updateChatShortcut({ storeId, id }, body),
  deleteShortcut: (storeId: string, id: string): Promise<void> =>
    deleteChatShortcut({ storeId, id }),

  departments: async (storeId: string): Promise<ChatDepartment[]> =>
    (await listChatDepartments({ storeId })).items,
  createDepartment: (storeId: string, body: CreateChatDepartmentRequest): Promise<ChatDepartment> =>
    createChatDepartment({ storeId }, body),
  updateDepartment: (
    storeId: string,
    id: string,
    body: UpdateChatDepartmentRequest,
  ): Promise<ChatDepartment> => updateChatDepartment({ storeId, id }, body),
  deleteDepartment: (storeId: string, id: string): Promise<void> =>
    deleteChatDepartment({ storeId, id }),

  bans: (storeId: string, cursor: string | undefined): Promise<ChatBanPage> =>
    listChatBans({ storeId }, { limit: 50, ...(cursor ? { cursor } : {}) }),
  createBan: (storeId: string, body: CreateChatBanRequest): Promise<ChatBan> =>
    createChatBan({ storeId }, body),
  deleteBan: (storeId: string, id: string): Promise<void> => deleteChatBan({ storeId, id }),

  triggers: async (storeId: string): Promise<ChatTrigger[]> =>
    (await listChatTriggers({ storeId })).items,
  createTrigger: (storeId: string, body: CreateChatTriggerRequest): Promise<ChatTrigger> =>
    createChatTrigger({ storeId }, body),
  updateTrigger: (
    storeId: string,
    id: string,
    body: UpdateChatTriggerRequest,
  ): Promise<ChatTrigger> => updateChatTrigger({ storeId, id }, body),
  deleteTrigger: (storeId: string, id: string): Promise<void> => deleteChatTrigger({ storeId, id }),
  /** Active code discounts a trigger can hand out (the composer's list, LC12). */
  discounts: (storeId: string): Promise<ChatDiscountOptions> => listChatDiscounts({ storeId }),

  profile: (storeId: string): Promise<MemberChatProfile> => getMyChatProfile({ storeId }),
  updateProfile: (
    storeId: string,
    body: UpdateMemberChatProfileRequest,
  ): Promise<MemberChatProfile> => updateMyChatProfile({ storeId }, body),
};
